import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, X, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = ['All', 'Living', 'Dining', 'Kitchen', 'Bath', 'Outdoor', 'Specialty'];

export const Portfolio = () => {
  const [portfolio, setPortfolio] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedIndex, setSelectedIndex] = useState(null);

  useEffect(() => {
    const fetchPortfolio = async () => {
      try {
        const res = await axios.get(`${API}/portfolio`);
        setPortfolio(res.data);
      } catch (error) {
        console.error('Error fetching portfolio:', error);
      }
    };
    fetchPortfolio();
  }, []);

  const filteredPortfolio = activeCategory === 'All'
    ? portfolio
    : portfolio.filter(p => p.category === activeCategory);

  const selectedProject = selectedIndex !== null ? filteredPortfolio[selectedIndex] : null;

  const goNext = useCallback(() => {
    if (selectedIndex !== null) {
      setSelectedIndex((selectedIndex + 1) % filteredPortfolio.length);
    }
  }, [selectedIndex, filteredPortfolio.length]);

  const goPrev = useCallback(() => {
    if (selectedIndex !== null) {
      setSelectedIndex((selectedIndex - 1 + filteredPortfolio.length) % filteredPortfolio.length);
    }
  }, [selectedIndex, filteredPortfolio.length]);

  const closeLightbox = useCallback(() => setSelectedIndex(null), []);

  useEffect(() => {
    if (selectedIndex === null) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [selectedIndex, closeLightbox, goNext, goPrev]);

  return (
    <div data-testid="portfolio-page">
      {/* Hero */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <img
          src="/assets/images/vicenzo-livingrm.jpg"
          alt="Portfolio"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 text-center text-white">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-sm tracking-[0.3em] uppercase mb-4"
          >
            Our Work
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-5xl md:text-7xl font-serif"
          >
            Portfolio
          </motion.h1>
        </div>
      </section>

      {/* Filter & Gallery */}
      <section className="section-padding bg-bone">
        <div className="container-custom">
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-4 mb-16">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                data-testid={`filter-${category.toLowerCase()}`}
                className={`px-6 py-3 text-sm tracking-widest uppercase transition-all duration-300 ${
                  activeCategory === category
                    ? 'bg-bronze text-white'
                    : 'bg-white border border-border text-foreground hover:border-bronze hover:text-bronze'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Gallery Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredPortfolio.map((project, index) => (
              <motion.div
                key={project.id}
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                onClick={() => setSelectedIndex(index)}
                data-testid={`portfolio-item-${project.id}`}
                className="group relative overflow-hidden cursor-pointer h-[300px] md:h-[350px] rounded-xl"
              >
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute bottom-0 left-0 right-0 p-8 translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                    <p className="text-xs tracking-widest uppercase text-coral mb-2">{project.category}</p>
                    <h3 className="text-2xl font-serif text-white mb-2">{project.title}</h3>
                    <p className="text-white/70 text-sm">{project.description}</p>
                  </div>
                </motion.div>
            ))}
          </div>

          {filteredPortfolio.length === 0 && (
            <div className="text-center py-16">
              <p className="text-muted-foreground">No projects found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeLightbox}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          >
            {/* Close */}
            <button
              onClick={closeLightbox}
              data-testid="lightbox-close"
              className="absolute top-6 right-6 z-10 text-white/70 hover:text-white transition-colors"
            >
              <X size={28} />
            </button>

            {/* Counter */}
            <div className="absolute top-7 left-1/2 -translate-x-1/2 text-white/50 text-sm tracking-widest">
              {selectedIndex + 1} / {filteredPortfolio.length}
            </div>

            {/* Prev */}
            <button
              onClick={(e) => { e.stopPropagation(); goPrev(); }}
              data-testid="lightbox-prev"
              className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-10 w-12 h-12 flex items-center justify-center text-white/60 hover:text-white transition-colors"
            >
              <ChevronLeft size={32} />
            </button>

            {/* Next */}
            <button
              onClick={(e) => { e.stopPropagation(); goNext(); }}
              data-testid="lightbox-next"
              className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-10 w-12 h-12 flex items-center justify-center text-white/60 hover:text-white transition-colors"
            >
              <ChevronRight size={32} />
            </button>

            {/* Content */}
            <motion.div
              key={selectedProject.id}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-6xl w-full px-16 md:px-24"
            >
              <img
                src={selectedProject.image}
                alt={selectedProject.title}
                data-testid="lightbox-image"
                className="w-full max-h-[75vh] object-contain"
              />
              <div className="mt-6 text-center">
                <p className="text-xs tracking-widest uppercase text-coral mb-2">{selectedProject.category}</p>
                <h3 className="text-2xl font-serif text-white">{selectedProject.title}</h3>
                <p className="text-white/60 mt-2 text-sm max-w-lg mx-auto">{selectedProject.description}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA */}
      <section className="section-padding bg-muted">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0 }}
            transition={{ duration: 0.7 }}
          >
            <h2 className="text-4xl md:text-5xl font-serif mb-6">Love What You See?</h2>
            <p className="text-muted-foreground mb-10 max-w-xl mx-auto">
              Let's discuss how we can bring this level of design excellence to your home.
            </p>
            <Link
              to="/contact"
              data-testid="portfolio-cta-btn"
              className="btn-primary inline-flex items-center gap-2"
            >
              Start Your Project
              <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
